import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/** Skim filter: pointer must dwell this long before MegaMenuHoverEvent fires. */
const HOVER_COMMIT_DELAY_MS = 150;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 * @property {HTMLElement | null} activeOverflowItem - The overflow item shown when the More trigger is active.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /**
   * @type {ResizeObserver | null}
   */
  #submenuResizeObserver = null;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #hoverDispatchTimer;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #submenuHeightRetryTimer;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #submenuHeightFrameFallback;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #deactivateTimer;

  /** Delay before closing so the pointer can reach the absolutely-positioned submenu. */
  static DEACTIVATE_DELAY_MS = 120;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    this.addEventListener('keydown', this.#onKeydown);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    this.removeEventListener('keydown', this.#onKeydown);
    document.body.removeEventListener('pointermove', this.#onPointerMove);
    if (this.#state.activeItem) {
      this.#stopPointerTracking(this.#state.activeItem);
    }
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.#cleanupMutationObserver();
    this.#cleanupSubmenuResizeObserver();
    clearTimeout(this.#hoverDispatchTimer);
    this.#hoverDispatchTimer = undefined;
    clearTimeout(this.#submenuHeightRetryTimer);
    this.#submenuHeightRetryTimer = undefined;
    clearTimeout(this.#submenuHeightFrameFallback);
    this.#submenuHeightFrameFallback = undefined;
    clearTimeout(this.#deactivateTimer);
    this.#deactivateTimer = undefined;
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);

  #overflowSubmenuListener = () => {
    this.#scheduleDeactivate();
  };

  /**
   * Close the open submenu on Escape and restore focus to its disclosure control.
   * @param {KeyboardEvent} event
   */
  #onKeydown = (event) => {
    if (event.key !== 'Escape') return;
    const item = this.#state.activeItem;
    if (!item) return;

    const control = this.#focusTargetAfterClose(item);
    this.#deactivate(item, { force: true });
    control.focus();
  };

  /**
   * Toggle a submenu from its dedicated disclosure button (click / Enter / Space).
   * @param {PointerEvent | MouseEvent} event
   */
  toggle = (event) => {
    const button = event.target;
    if (!(button instanceof HTMLElement)) return;

    const item = findMenuItem(button);
    if (!item) return;

    const isExpanded = button.getAttribute('aria-expanded') === 'true';
    if (isExpanded) {
      // Close whatever is currently active. In the overflow panel the initially
      // selected item is tracked as `activeOverflowItem` while the More trigger is
      // the `activeItem`, so deactivating `item` directly would no-op; deactivate
      // the active item instead.
      this.#deactivate(this.#state.activeItem ?? item, { force: true });
      // Closing the overflow panel hides the focused disclosure, so return focus
      // to the visible More trigger; a top-level disclosure stays put.
      const focusTarget = this.#focusTargetAfterClose(item);
      if (focusTarget !== button) focusTarget.focus();
    } else {
      this.activate(event);
    }
  };

  /**
   * The control that should receive focus after a submenu closes. Overflow items
   * live in a panel that is hidden on close, so focus returns to the always-visible
   * More trigger instead of a now-inert control inside the panel.
   * @param {HTMLElement} item
   * @returns {HTMLElement}
   */
  #focusTargetAfterClose(item) {
    const listItem = item.closest('.menu-list__list-item');
    const slot = listItem instanceof HTMLElement ? listItem.slot : '';
    if (slot === 'overflow' || slot === 'more') {
      const moreButton = this.querySelector('[slot="more"] [ref="menuitem"]');
      if (moreButton instanceof HTMLElement) return moreButton;
    }
    return this.#expandableFor(item);
  }

  /**
   * Return the element that carries the `aria-expanded` state for a menu item.
   * @param {HTMLElement} item
   * @returns {HTMLElement}
   */
  #expandableFor(item) {
    const listItem = item.closest('.menu-list__list-item');
    const disclosure = listItem?.querySelector('[ref="disclosure[]"]');
    return disclosure instanceof HTMLElement ? disclosure : item;
  }

  /**
   * Hide a submenu panel and remove stale active markers.
   * @param {HTMLElement | null | undefined} submenu
   */
  #closeSubmenuPanel(submenu) {
    if (!(submenu instanceof HTMLElement)) return;

    delete submenu.dataset.active;
    submenu.inert = true;
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
    activeOverflowItem: null,
  };

  /**
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #pointerIdleTimer;

  /**
   * Last known pointer position for Safari hit-test reconciliation.
   * @type {{ x: number, y: number }}
   */
  #lastPointer = { x: 0, y: 0 };

  /**
   * Update the safety box idle state on the active menu item.
   * @param {PointerEvent} event
   */
  #onPointerMove = (event) => {
    const activeLink = this.#state.activeItem;
    if (!activeLink) return;

    this.#lastPointer.x = event.clientX;
    this.#lastPointer.y = event.clientY;

    const moving = Math.abs(event.movementX) >= 1 || event.movementY >= 1;
    activeLink.dataset.safetyBox = `${moving}`;

    clearTimeout(this.#pointerIdleTimer);
    if (moving) {
      this.#pointerIdleTimer = setTimeout(() => {
        if (this.#state.activeItem) {
          this.#state.activeItem.dataset.safetyBox = 'false';
          this.#reconcilePointerTarget();
        }
      }, 50);
    } else {
      this.#reconcilePointerTarget();
    }
  };

  /**
   * Check if the pointer is over a different menu item and trigger activation if so.
   * Works around Safari not re-evaluating hit targets after pseudo-element changes.
   */
  #reconcilePointerTarget() {
    const { x, y } = this.#lastPointer;
    requestAnimationFrame(() => {
      const target = document.elementFromPoint(x, y);
      if (!target) return;
      const listItem = target.closest('.menu-list__list-item');
      if (listItem && !listItem.contains(this.#state.activeItem)) {
        listItem.dispatchEvent(new PointerEvent('pointerenter', { bubbles: false }));
      }
    });
  }

  /**
   * Begin pointer tracking for the safety box on the newly active item.
   * @param {HTMLElement} item
   * @param {HTMLElement | null} previousItem
   */
  #startPointerTracking(item, previousItem) {
    if (previousItem) {
      this.#stopPointerTracking(previousItem);
    } else {
      document.body.addEventListener('pointermove', this.#onPointerMove);
    }

    const rect = item.getBoundingClientRect();
    const isOverlap = this.headerComponent?.hasAttribute('data-submenu-overlap-bottom-row');
    const boundary = isOverlap ? this.headerComponent?.querySelector('.header__row--top') : this.headerComponent;
    item.style.setProperty('--box-height', `${(boundary?.getBoundingClientRect().bottom ?? 0) - rect.top}px`);
  }

  /**
   * Stop pointer tracking and remove all safety box properties from an item.
   * @param {HTMLElement} item
   */
  #stopPointerTracking(item) {
    window.clearTimeout(this.#pointerIdleTimer);
    this.#pointerIdleTimer = undefined;
    item.style.removeProperty('--box-height');
    delete item.dataset.safetyBox;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  /**
   * Find the first overflowing menu item shown by the More trigger.
   * @returns {HTMLElement | null}
   */
  #getFirstOverflowMenuItem() {
    const menuItem = this.refs.overflowMenu?.querySelector('[slot="overflow"] [ref="menuitem"]');
    return menuItem instanceof HTMLElement ? menuItem : null;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Measure rendered submenu content height (not content-visibility placeholders).
   * @param {HTMLElement} submenu
   * @returns {number}
   */
  #getMeasuredSubmenuHeight(submenu) {
    const inner = submenu.querySelector('.menu-list__submenu-inner');
    const measureTarget = inner instanceof HTMLElement ? inner : submenu;
    const contentRoot =
      measureTarget.querySelector('.mega-menu__grid--ak-flex, .mega-menu__list, .mega-menu__grid') ??
      measureTarget.firstElementChild;

    const heights = [
      measureTarget.scrollHeight,
      measureTarget.offsetHeight,
      submenu.scrollHeight,
      submenu.offsetHeight,
    ];

    if (contentRoot instanceof HTMLElement) {
      heights.push(contentRoot.scrollHeight, contentRoot.offsetHeight);
    }

    return heights.reduce((max, value) => Math.max(max, value), 0);
  }

  /**
   * Compute the open submenu height for clip-path and underlay animation.
   * @param {HTMLElement | null | undefined} submenu
   * @param {HTMLElement} item
   * @param {boolean} isDefaultSlot
   * @param {boolean} hasSubmenu
   * @returns {number}
   */
  #computeSubmenuHeight(submenu, item, isDefaultSlot, hasSubmenu) {
    if (!submenu) return 0;

    let finalHeight = this.#getMeasuredSubmenuHeight(submenu);

    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight, finalHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    return finalHeight;
  }

  /** @type {number} */
  #lastOpenSubmenuHeight = 0;

  /**
   * Write submenu height CSS variables on the header.
   * @param {number} finalHeight
   */
  #applySubmenuHeight(finalHeight) {
    if (!this.headerComponent) return;

    // Never collapse an already-open panel to 0 from a transient measure (image
    // decode / clip-path mid-transition). That paints a blank underlay.
    let height = finalHeight;
    if (height <= 0 && this.#lastOpenSubmenuHeight > 0 && this.#state.activeItem) {
      height = this.#lastOpenSubmenuHeight;
    }
    if (height > 0) this.#lastOpenSubmenuHeight = height;

    const headerVisibleHeight = this.#getHeaderVisibleHeight();
    // Offset mega content below the clip-path band (header). Do not derive this from
    // (full-open - submenu-height): those two vars can update a frame apart and yank
    // content upward (blank) or leave transform:none (heads clipped by clip-path).
    this.headerComponent.style.setProperty('--submenu-content-offset', `${headerVisibleHeight}px`);
    this.headerComponent.style.setProperty('--submenu-height', `${height}px`);
    this.#setFullOpenHeaderHeight(height, headerVisibleHeight);

    // Keep first-open content in the scrollport after height/transform writes.
    const activeSubmenu = this.#state.activeItem ? findSubmenu(this.#state.activeItem) : null;
    const inner = activeSubmenu?.querySelector('.menu-list__submenu-inner');
    if (inner instanceof HTMLElement && inner.scrollTop !== 0) {
      inner.scrollTop = 0;
    }
  }

  /**
   * Measure submenu height after layout, images, and content-visibility settle.
   * Retries briefly when height is still zero (blank panel / clip-path bug).
   * @param {HTMLElement} submenu
   * @param {HTMLElement} item
   * @param {boolean} isDefaultSlot
   * @param {boolean} hasSubmenu
   * @param {number} [attempt]
   */
  #syncSubmenuHeight(submenu, item, isDefaultSlot, hasSubmenu, attempt = 0) {
    if (!this.headerComponent || submenu.dataset.active === undefined) return;

    // Closed panels use content-visibility:auto; force paint before measuring so the
    // first hover does not open a blank underlay (clip-path height still 0).
    submenu.style.setProperty('content-visibility', 'visible');

    // Measure once up front: while --submenu-height is 0 the clip-path collapses the
    // panel, so a delayed first measurement shows an empty dropdown.
    const immediateHeight = this.#computeSubmenuHeight(submenu, item, isDefaultSlot, hasSubmenu);
    // Provisional floor keeps the clip open while images/layout settle on first open.
    const openHeight = immediateHeight > 0 ? immediateHeight : 320;
    this.#applySubmenuHeight(openHeight);

    let refined = false;
    const refine = () => {
      if (refined) return;
      refined = true;
      clearTimeout(this.#submenuHeightFrameFallback);
      this.#submenuHeightFrameFallback = undefined;

      if (submenu.dataset.active === undefined) return;

      const finalHeight = this.#computeSubmenuHeight(submenu, item, isDefaultSlot, hasSubmenu);

      if (finalHeight > 0) {
        this.#applySubmenuHeight(finalHeight);
        clearTimeout(this.#submenuHeightRetryTimer);
        this.#submenuHeightRetryTimer = undefined;
        return;
      }

      if (attempt >= 20) {
        // Keep the provisional floor rather than collapsing back to a blank clip.
        if (immediateHeight <= 0) this.#applySubmenuHeight(320);
        return;
      }

      clearTimeout(this.#submenuHeightRetryTimer);
      this.#submenuHeightRetryTimer = setTimeout(() => {
        this.#syncSubmenuHeight(submenu, item, isDefaultSlot, hasSubmenu, attempt + 1);
      }, 50);
    };

    requestAnimationFrame(() => requestAnimationFrame(refine));

    // Animation frames are throttled in background/occluded tabs, which would leave
    // the panel clipped until the next reflow.
    clearTimeout(this.#submenuHeightFrameFallback);
    this.#submenuHeightFrameFallback = setTimeout(refine, 120);
  }

  /**
   * Keep submenu height in sync when promo images load or content reflows.
   * @param {HTMLElement} submenu
   * @param {HTMLElement} item
   * @param {boolean} isDefaultSlot
   * @param {boolean} hasSubmenu
   */
  #startSubmenuHeightTracking(submenu, item, isDefaultSlot, hasSubmenu) {
    this.#cleanupSubmenuResizeObserver();

    const syncNow = () => {
      if (submenu.dataset.active === undefined) return;
      this.#syncSubmenuHeight(submenu, item, isDefaultSlot, hasSubmenu);
    };
    const resync = debounce(syncNow, 50);

    if (typeof ResizeObserver !== 'undefined') {
      this.#submenuResizeObserver = new ResizeObserver(resync);
      this.#submenuResizeObserver.observe(submenu);
      const inner = submenu.querySelector('.menu-list__submenu-inner');
      if (inner instanceof HTMLElement) {
        this.#submenuResizeObserver.observe(inner);
      }
    }

    submenu.querySelectorAll('img').forEach((img) => {
      if (!(img instanceof HTMLImageElement) || img.complete) return;
      img.addEventListener('load', resync, { once: true });
      img.addEventListener('error', resync, { once: true });
    });

    syncNow();
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    if (!(event.target instanceof Element) || !this.headerComponent) return;

    clearTimeout(this.#deactivateTimer);
    this.#deactivateTimer = undefined;

    const item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    // Derive the slot context from the list item rather than `event.target`.
    const listItem = item.closest('.menu-list__list-item');
    const slot = listItem instanceof HTMLElement ? listItem.slot : '';
    const isMoreTrigger = slot === 'more';
    const isDefaultSlot = slot === '';
    const overflowItem = isMoreTrigger ? this.#getFirstOverflowMenuItem() : null;

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;
    const previouslyActiveOverflowItem = this.#state.activeOverflowItem;

    if (previouslyActiveItem) {
      this.#expandableFor(previouslyActiveItem).ariaExpanded = 'false';
      this.#closeSubmenuPanel(findSubmenu(previouslyActiveItem));
    }
    if (previouslyActiveOverflowItem && previouslyActiveOverflowItem !== previouslyActiveItem) {
      this.#expandableFor(previouslyActiveOverflowItem).ariaExpanded = 'false';
      this.#closeSubmenuPanel(findSubmenu(previouslyActiveOverflowItem));
    }

    this.#state.activeItem = item;
    this.#state.activeOverflowItem = overflowItem;
    this.ariaExpanded = 'true';
    this.#expandableFor(item).ariaExpanded = 'true';
    if (overflowItem && overflowItem !== item) {
      this.#expandableFor(overflowItem).ariaExpanded = 'true';
      // The More trigger auto-opens the first overflow item's submenu, so expose it.
      const activeOverflowSubmenu = findSubmenu(overflowItem);
      if (activeOverflowSubmenu) activeOverflowSubmenu.inert = false;
    }

    const overflowItemSubmenu = isMoreTrigger ? findSubmenu(overflowItem) : null;
    let submenu = findSubmenu(item) || (overflowItemSubmenu ? this.overflowMenu : null);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      clearTimeout(this.#hoverDispatchTimer);
      this.#hoverDispatchTimer = undefined;
      const committedItem = item;
      if (event instanceof FocusEvent) {
        this.dispatchEvent(new MegaMenuHoverEvent());
      } else {
        this.#hoverDispatchTimer = setTimeout(() => {
          this.#hoverDispatchTimer = undefined;
          if (this.#state.activeItem === committedItem) {
            this.dispatchEvent(new MegaMenuHoverEvent());
          }
        }, HOVER_COMMIT_DELAY_MS);
      }

      // Only one mega menu panel should stay visible at a time. When `submenu` is the
      // overflow panel the item's own submenu lives inside it, so keep descendants open.
      const openPanel = submenu;
      this.querySelectorAll('.menu-list__submenu[data-active]').forEach((panel) => {
        if (panel === openPanel || openPanel.contains(panel)) return;
        this.#closeSubmenuPanel(/** @type {HTMLElement} */ (panel));
      });

      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Expose the open submenu to focus and assistive tech. Collapsed submenus stay
      // inert so their contents cannot be reached (visibility:hidden alone can be
      // overridden by descendants that re-assert visibility).
      submenu.inert = false;

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        this.#syncSubmenuHeight(submenu, item, isDefaultSlot, hasSubmenu);
      });
      this.#submenuMutationObserver.observe(submenu, { childList: true, subtree: true });

      // Auto-disconnect after idle; height stays synced via ResizeObserver until close.
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 3000);

      this.#startSubmenuHeightTracking(submenu, item, isDefaultSlot, hasSubmenu);
    }

    if (!submenu) {
      this.#applySubmenuHeight(0);
    }

    this.style.setProperty('--submenu-opacity', '1');
    this.#startPointerTracking(item, previouslyActiveItem);
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const menu = findSubmenu(this.#state.activeItem);
    // Keep the submenu open while focus moves between the link, its disclosure
    // button, and the submenu contents — i.e. anywhere inside the same list item.
    const isMovingWithinItem =
      event.relatedTarget instanceof Node &&
      event.target instanceof Element &&
      event.target.contains(event.relatedTarget);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Element && Boolean(event.relatedTarget.closest('[slot="overflow"]'));

    if (isMovingWithinItem || isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) {
      if (this.#state.activeItem) {
        this.#stopPointerTracking(this.#state.activeItem);
      }
      return;
    }

    this.#scheduleDeactivate();
  }

  /**
   * Whether the open submenu should stay open based on current pointer position.
   * @returns {boolean}
   */
  #shouldKeepSubmenuOpen() {
    const item = this.#state.activeItem;
    if (!item) return false;

    const listItem = item.closest('.menu-list__list-item');
    const submenu = findSubmenu(item) || (item.closest('[slot="more"]') ? this.overflowMenu : null);

    if (listItem instanceof HTMLElement && listItem.matches(':hover')) return true;
    if (submenu instanceof HTMLElement && submenu.matches(':hover')) return true;
    if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) return true;

    return false;
  }

  /**
   * Defer submenu close so diagonal moves from nav link to panel do not blank the menu.
   */
  #scheduleDeactivate() {
    clearTimeout(this.#deactivateTimer);
    this.#deactivateTimer = setTimeout(() => {
      this.#deactivateTimer = undefined;
      if (this.#shouldKeepSubmenuOpen()) return;
      this.#deactivate(undefined, { force: false });
    }, HeaderMenu.DEACTIVATE_DELAY_MS);
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem, { force = false } = {}) => {
    clearTimeout(this.#deactivateTimer);
    this.#deactivateTimer = undefined;

    if (!item || item != this.#state.activeItem) return;

    // Don't deactivate if the overflow menu or overflow list is still being hovered,
    // unless the close was explicitly requested (Escape or the disclosure toggle),
    // which must close regardless of pointer position.
    const openSubmenu = findSubmenu(item) || (item.closest('[slot="more"]') ? this.overflowMenu : null);
    if (
      !force &&
      (this.overflowListHovered ||
        this.overflowMenu?.matches(':hover') ||
        openSubmenu?.matches(':hover'))
    ) {
      return;
    }

    clearTimeout(this.#hoverDispatchTimer);
    this.#hoverDispatchTimer = undefined;
    clearTimeout(this.#submenuHeightRetryTimer);
    this.#submenuHeightRetryTimer = undefined;
    clearTimeout(this.#submenuHeightFrameFallback);
    this.#submenuHeightFrameFallback = undefined;
    this.#cleanupSubmenuResizeObserver();

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.headerComponent?.style.setProperty('--submenu-content-offset', '0px');
    this.#setFullOpenHeaderHeight(0, 0);
    this.#lastOpenSubmenuHeight = 0;
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = openSubmenu;
    if (submenu instanceof HTMLElement) {
      submenu.style.removeProperty('content-visibility');
    }
    const activeOverflowItem = this.#state.activeOverflowItem;

    document.body.removeEventListener('pointermove', this.#onPointerMove);
    this.#stopPointerTracking(item);

    this.#state.activeItem = null;
    this.#state.activeOverflowItem = null;
    this.ariaExpanded = 'false';
    this.#expandableFor(item).ariaExpanded = 'false';
    if (activeOverflowItem && activeOverflowItem !== item) {
      this.#expandableFor(activeOverflowItem).ariaExpanded = 'false';
      const activeOverflowSubmenu = findSubmenu(activeOverflowItem);
      if (activeOverflowSubmenu) activeOverflowSubmenu.inert = true;
    }

    // Remove active state from submenu after animation completes
    if (submenu) {
      this.#closeSubmenuPanel(submenu);
    }
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    };

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Read the visible header height before submenu height writes invalidate layout.
   * @returns {number}
   */
  #getHeaderVisibleHeight() {
    if (!this.headerComponent) return 0;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    return isOverlapSituation && this.headerComponent.offsetHeight > 0
      ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
      : this.headerComponent.offsetHeight;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   * @param {number} headerVisibleHeight
   */
  #setFullOpenHeaderHeight(submenuHeight, headerVisibleHeight) {
    if (!this.headerComponent) return;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + headerVisibleHeight;

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }

  #cleanupSubmenuResizeObserver() {
    this.#submenuResizeObserver?.disconnect();
    this.#submenuResizeObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  // `element` may be the list item itself (hover/focus routed via the `<li>`) or a
  // descendant such as the disclosure button (click routed via the button).
  const listItem = element.closest('.menu-list__list-item') ?? element;
  return listItem.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const listItem = element?.closest?.('.menu-list__list-item');
  const submenu = listItem?.querySelector(':scope > [ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
